import {useState} from "react";
import {useSetRecoilState} from "recoil";
import {useTranslation} from "react-i18next";
import {browserSupportsWebAuthn, startRegistration} from "@simplewebauthn/browser";
import {createLog} from "../api/restful/logs/logs";
import {globalNotificationState} from "./global_state";
import useTwoFactorGuard from "./useTwoFactorGuard";
import {markCannotEnrolHere, markPasskeyWorkedHere} from "./passkeyPromptState";
import {
    finishPasskeyRegistrationMutation,
    startPasskeyRegistrationMutation,
} from "../api/graphql/userPasskey";

const platformAuthenticatorAvailable = async () => {
    try {
        return await window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch (error) {
        return false;
    }
};

// nothing within reach can satisfy the options, so another device is the only way forward
const DEVICE_CANNOT = [
    "ERROR_AUTHENTICATOR_MISSING_DISCOVERABLE_CREDENTIAL_SUPPORT",
    "ERROR_AUTHENTICATOR_MISSING_USER_VERIFICATION_SUPPORT",
    "ERROR_AUTHENTICATOR_NO_SUPPORTED_PUBKEYCREDPARAMS_ALG",
];
const DEVICE_CANNOT_NAMES = ["ConstraintError", "NotSupportedError"];

// ours to fix, so the client is sent to support rather than chasing their screen lock
const OUR_FAULT = [
    "ERROR_INVALID_RP_ID",
    "ERROR_INVALID_DOMAIN",
    "ERROR_MALFORMED_PUBKEYCREDPARAMS",
    "ERROR_INVALID_USER_ID_LENGTH",
    "ERROR_AUTO_REGISTER_USER_VERIFICATION_FAILURE",
];
const OUR_FAULT_NAMES = ["SecurityError", "TypeError"];

// enrolling is itself a guarded action, so the guard raises its own challenge first
const usePasskeyEnrolment = ({layoutKey = "", onEnrolled, onBlocked} = {}) => {
    const {t} = useTranslation();
    const setNotificationMessage = useSetRecoilState(globalNotificationState);
    const guard = useTwoFactorGuard();
    const [busy, setBusy] = useState(false);

    const notify = (status, msg, key) =>
        setNotificationMessage({status, title: "", msg, key: key + layoutKey});

    const finishRegistration = async (options, name = null) => {
        let attestation;
        const startedAt = Date.now();

        try {
            // the browser prompts here; the private key never leaves the device
            attestation = await startRegistration({
                optionsJSON: JSON.parse(options.options),
            });
        } catch (error) {
            const code = error?.code;
            const name = error?.name;

            createLog({
                status: "warning",
                module: "TwoFactor",
                submodule: "Passkey",
                function: "enrolCeremony",
                description: `Passkey ceremony failed: ${name || "unknown"}`,
                logData: {name, code, message: error?.message, elapsedMs: Date.now() - startedAt},
                triggerBy: "user",
            });

            if (code === "ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED" || name === "InvalidStateError") {
                notify(
                    "info",
                    t("passkey.alreadyOnDevice", {
                        defaultValue: "This device already has a passkey for your account",
                    }),
                    "passkeyAlreadyHere",
                );
                return false;
            }

            if (DEVICE_CANNOT.includes(code) || DEVICE_CANNOT_NAMES.includes(name)) {
                notify(
                    "info",
                    t("passkey.deviceCannot", {
                        defaultValue:
                            "This device cannot create a passkey. Open the site on another device to continue",
                    }),
                    "passkeyDeviceCannot",
                );
                markCannotEnrolHere();
                onBlocked?.();
                return false;
            }

            if (OUR_FAULT.includes(code) || OUR_FAULT_NAMES.includes(name)) {
                console.error("passkey registration blocked:", code || name, error?.message);
                notify(
                    "error",
                    t("passkey.setupUnavailable", {
                        defaultValue:
                            "Passkey setup is unavailable right now. Please contact support",
                    }),
                    "passkeySetupUnavailable",
                );
                return false;
            }

            if (code === "ERROR_AUTHENTICATOR_GENERAL_ERROR" || name === "UnknownError") {
                notify(
                    "error",
                    t("passkey.deviceFailed", {
                        defaultValue:
                            "Your device could not finish creating the passkey. Please try again",
                    }),
                    "passkeyDeviceFailed",
                );
                return false;
            }

            // NotAllowedError covers dismissed, timed out and never answered alike
            if (name === "NotAllowedError" || name === "AbortError") {
                // no screen lock is not "cannot enrol": the qr route needs no
                // platform authenticator, so this must never write the 30-day mark
                if (!(await platformAuthenticatorAvailable())) {
                    notify(
                        "info",
                        t("passkey.deviceUnavailable", {
                            defaultValue:
                                "This device cannot create a passkey right now. Check your screen lock or fingerprint settings, or use your phone instead",
                        }),
                        "passkeyDeviceUnavailable",
                    );

                    return false;
                }

                console.log("passkey registration NotAllowedError:", error?.message);

                // timing cannot tell a refusal from a device that cannot manage one
                notify(
                    "info",
                    t("passkey.setupCancelled", {
                        defaultValue: "Passkey setup was cancelled. Nothing was added",
                    }),
                    "passkeySetupCancelled",
                );

                return false;
            }

            console.error("passkey registration failed:", error);

            if (!browserSupportsWebAuthn()) {
                notify(
                    "info",
                    t("twoFactor.passkeyBrowserHint", {
                        defaultValue: "This browser doesn't support passkeys. Open this page in Chrome to add one.",
                    }),
                    "passkeyBrowserUnsupported",
                );
                markCannotEnrolHere();
                onBlocked?.();
                return false;
            }

            notify("error", t("admin.err.system_error"), "passkeyPromptException");
            return false;
        }

        const finished = await finishPasskeyRegistrationMutation(attestation, name);

        if (finished?.finishPasskeyRegistration?.__typename === "BaseError") {
            createLog({
                status: "warning",
                module: "TwoFactor",
                submodule: "Passkey",
                function: "finishRegistration",
                description: "Passkey finish rejected by the server",
                logData: {errKey: finished.finishPasskeyRegistration.errKey},
                triggerBy: "user",
            });
            notify("error", t(finished.finishPasskeyRegistration.errKey), "passkeyFinishError");
            return false;
        }

        notify("success", t("passkey.added", {defaultValue: "Passkey added"}), "passkeyAdded");
        markPasskeyWorkedHere();
        await onEnrolled?.();
        return true;
    };

    const enrol = async () => {
        // checked before the guard, or an unsupported browser burns an otp to learn this
        if (!browserSupportsWebAuthn()) {
            createLog({
                status: "warning",
                module: "TwoFactor",
                submodule: "Passkey",
                function: "enrol",
                description: "Browser has no WebAuthn",
                triggerBy: "user",
            });
            notify(
                "info",
                t("twoFactor.passkeyBrowserHint", {
                    defaultValue: "This browser doesn't support passkeys. Open this page in Chrome to add one.",
                }),
                "passkeyBrowserUnsupported",
            );
            markCannotEnrolHere();
            onBlocked?.();
            return false;
        }

        setBusy(true);

        try {
            const options = await guard.run({
                action: "passkeyEnrolment",
                attempt: async (twoFactorChallengeId) => {
                    const started = await startPasskeyRegistrationMutation(twoFactorChallengeId);

                    return started?.startPasskeyRegistration;
                },
            });

            if (!options) return false;

            if (options?.__typename === "BaseError") {
                createLog({
                    status: "warning",
                    module: "TwoFactor",
                    submodule: "Passkey",
                    function: "startRegistration",
                    description: "Passkey start rejected by the server",
                    logData: {errKey: options?.errKey || null},
                    triggerBy: "user",
                });
                notify("error", t(options?.errKey || "admin.err.system_error"), "passkeyStartError");
                return false;
            }

            return await finishRegistration(options);
        } catch (error) {
            createLog({
                status: "error",
                module: "TwoFactor",
                submodule: "Passkey",
                function: "enrol",
                description: "Passkey enrolment threw",
                logData: {error},
                triggerBy: "user",
            });
            notify("error", t("admin.err.system_error"), "passkeyChallengeException");
            return false;
        } finally {
            setBusy(false);
        }
    };

    return {enrol, busy};
};

export default usePasskeyEnrolment;
